import Swal from "sweetalert2";
import { append_active_organization_form_data, append_active_organization_id } from "@/utils/organization_profile";

function show_service_toast({ icon, title = "", text, timer = 2500 }) {
    Swal.fire({
        icon,
        title,
        text,
        position: "bottom-end",
        toast: true,
        showConfirmButton: false,
        timer,
    });
}

function find_modal_product_type(product_types = [], modal_type = "") {
    return product_types.find(
        (type) => String(type.name || "").toLowerCase() === modal_type.toLowerCase()
    );
}

function append_media_files(formData, files = []) {
    files.forEach((file) => {
        formData.append("media_files", file);
    });
}

function append_deleted_image_ids(formData, previews = []) {
    previews
        .filter((preview) => preview._delete && preview.id)
        .map((preview) => preview.id)
        .forEach((id) => {
            formData.append("delete_image_ids", id);
        });
}

function create_base_service_form_data(context, product_type) {
    const formData = new FormData();
    formData.append("name", context.item_form.name);
    formData.append("price", context.item_form.price);
    formData.append("description", context.item_form.description || "");
    formData.append("base_profile", context.base_profile_id);
    formData.append("product_type_id", product_type.id);
    formData.append("category_id", context.selected_subcategory || "");
    formData.append("attributes", JSON.stringify(context.item_form.attributes || {}));
    append_active_organization_form_data(formData);
    return formData;
}

function has_service_publish_image(context) {
    return (context.upload_files || []).some((file) => file?.type?.startsWith("image/"))
        || (context.previews || []).some((preview) => preview?.type === "image" && !preview?._delete);
}

function append_create_service_details(formData, item_form) {
    if (item_form.service_type_id) formData.append("service_type_id", item_form.service_type_id);
    if (item_form.service_location) formData.append("location", item_form.service_location);
}

function create_update_service_form_data(context) {
    const formData = new FormData();
    formData.append("product_id", context.update_item_form.product_id);
    formData.append("name", context.update_item_form.name);
    formData.append("price", context.update_item_form.price);
    formData.append("description", context.update_item_form.description);
    formData.append("base_profile_id", context.base_profile_id);
    formData.append("category_id", context.update_item_form.subcategory_id || "");
    formData.append("service_type_id", context.update_item_form.service_type_id);
    formData.append("location", context.update_item_form.location);
    formData.append("attributes", JSON.stringify(context.update_item_form.attributes || {}));
    append_active_organization_form_data(formData);
    return formData;
}

function build_next_catalogue_items(items = [], updated_product) {
    const next_items = [...items];
    const product_index = next_items.findIndex((product) => product.id === updated_product.id);
    if (product_index === -1) return [...next_items, updated_product];
    next_items.splice(product_index, 1, updated_product);
    return next_items;
}

function append_service_attribute(attributes, target_attributes) {
    const key = attributes.attribute_type?.name || attributes.attribute_type;
    const cleanKey = key.toLowerCase();
    if (!Reflect.get(target_attributes, cleanKey)) Reflect.set(target_attributes, cleanKey, []);
    Reflect.get(target_attributes, cleanKey).push(attributes.value);
}

function get_edit_service_form(product) {
    return {
        product_id: product.id,
        name: product.name,
        price: product.price,
        description: product.description,
        subcategory_id: product.category?.id || "",
        attributes: {},
        service_type_id: product.service_details?.service_type_id || "",
        service_type: product?.service_details?.type_name,
        location: product?.service_details?.location || ""
    };
}

function get_existing_service_previews(images = []) {
    return images.map((img) => ({
        type: "image",
        src: img.image_url,
        id: img.id,
        _delete: false,
    }));
}

export const services_catalogue_methods = {
    create_product(should_publish = false) {
        if (this.validate_item_form()) return;

        if (should_publish && !has_service_publish_image(this)) {
            this.handle_missing_publish_image();
            return;
        }

        const product_type = find_modal_product_type(this.product_types, this.modalType);
        if (!product_type?.id) {
            show_service_toast({ icon: "error", title: "Error", text: "Could not determine service type." });
            return;
        }

        this.isLoading = true;
        this.create_action_loading = should_publish ? "publish" : "draft";
        let publish_pending = false;
        const formData = create_base_service_form_data(this, product_type);
        formData.append("is_published", should_publish ? "true" : "false");
        append_create_service_details(formData, this.item_form);
        append_media_files(formData, this.upload_files);

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/create_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
            },
            data: formData,
            processData: false,
            contentType: false,
            success: (response) => {
                if (response.status === "success") {
                    publish_pending = should_publish && !response.data?.is_published;
                }
                this.handle_create_product_response(response, should_publish);
            },
            error: (xhr) => {
                show_service_toast({ icon: "error", title: "Error", text: xhr.responseText || "Upload failed." });
            },
            complete: () => {
                if (!publish_pending) {
                    this.isLoading = false;
                    this.create_action_loading = null;
                }
            }
        });
    },
    handle_create_product_response(response, should_publish = false) {
        if (response.status !== "success") {
            show_service_toast({ icon: "error", title: "Error", text: response.message || "Failed to create service." });
            return;
        }
        this.sync_catalogue_items([...this.profile_products, response.data]);
        if (should_publish && !response.data?.is_published) {
            this.publish_created_service(response.data);
            return;
        }
        this.finish_create_service(response.data?.is_published);
    },
    publish_created_service(service) {
        const base_profile_id = service?.base_profile_id || service?.base_profile?.id || this.base_profile_id;

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/publish_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
                "Content-Type": "application/json"
            },
            data: JSON.stringify(append_active_organization_id({
                product_id: service.id,
                base_profile_id
            })),
            success: (response) => {
                if (response.status === "success") {
                    service.is_published = response.is_published;
                    this.sync_catalogue_items([...this.profile_products]);
                    this.finish_create_service(response.is_published);
                    return;
                }

                this.finish_create_service(false, response.message || "Service saved as draft, but publishing failed.");
            },
            error: (xhr) => {
                this.finish_create_service(false, xhr.responseJSON?.message || "Service saved as draft, but publishing failed.");
            },
            complete: () => {
                this.isLoading = false;
                this.create_action_loading = null;
            }
        });
    },
    finish_create_service(is_published = false, warning_text = "") {
        this.reset_product_form();
        $("#addItemModal").modal("hide");
        show_service_toast({
            icon: warning_text ? "warning" : "success",
            title: warning_text ? "Saved as draft" : "Success",
            text: warning_text || (is_published ? "Service published successfully!" : "Service saved as draft."),
            timer: warning_text ? 2500 : 1000,
        });
    },
    handle_missing_publish_image() {
        this.errors = {
            ...this.errors,
            product_images: "Add at least one image before publishing, or save this service as draft.",
        };
        this.scroll_to_service_images();
    },
    scroll_to_service_images() {
        this.$nextTick(() => {
            const image_section = document.getElementById("add-product-images-section");
            if (!image_section) return;

            image_section.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    },
    open_manage_modal(product) {
        this.modal_product = product;
        this.isEditing = false;
        this.previews = [];
        this.upload_files = [];
        this.active_modal_image_index = 0;
        $("#manageProductModal").modal("show");
    },
    toggle_publish(product) {
        const previous_state = product.is_published;
        product.is_published = !product.is_published;
        const base_profile_id = product?.base_profile_id || product?.base_profile?.id || this.base_profile_id;

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/publish_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
                "Content-Type": "application/json"
            },
            data: JSON.stringify(append_active_organization_id({
                product_id: product.id,
                base_profile_id
            })),
            success: (response) => {
                if (response.status === "success") {
                    const updated_product = {
                        ...product,
                        ...(response.data || {}),
                        is_published: response.is_published,
                    };
                    this.upsert_catalogue_listing_from_realtime?.(updated_product);
                    product.is_published = response.is_published;
                    this.modal_product = updated_product;
                    return;
                }

                product.is_published = previous_state;
                Swal.fire({
                    icon: "error",
                    text: response.message || "Failed to update service status.",
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                });
            },
            error: (xhr) => {
                product.is_published = previous_state;
                Swal.fire({
                    icon: "error",
                    text: xhr.responseJSON?.message || "Failed to update service status.",
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                });
            }
        });
    },
    edit_product(product) {
        this.isEditing = true;
        this.modal_product = product;
        this.update_item_form = get_edit_service_form(product);
        this.selected_category = product.category;
        this.fetch_categories();

        if (this.selected_category) this.fetch_subcategories(this.selected_category);

        (product.attributes || []).forEach((attr) => {
            append_service_attribute(attr, this.update_item_form.attributes);
        });

        this.upload_files = [];
        this.previews = get_existing_service_previews(product.images || []);
    },
    update_product() {
        if (!this.update_item_form.product_id) return;

        this.isLoading = true;
        const formData = create_update_service_form_data(this);
        append_media_files(formData, this.upload_files);
        append_deleted_image_ids(formData, this.previews);

        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_product/`,
            method: "POST",
            headers: {
                Authorization: `Token ${this.getToken()}`,
            },
            data: formData,
            processData: false,
            contentType: false,
            success: (response) => this.handle_update_product_response(response),
            error: () => {
                show_service_toast({
                    icon: "error",
                    title: "Error",
                    text: "Failed to update service. Please try again.",
                });
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    },
    handle_update_product_response(response) {
        if (response.status !== "success") {
            show_service_toast({ icon: "error", title: "Error", text: response.message || "Failed to update service" });
            return;
        }

        this.sync_catalogue_items(build_next_catalogue_items(this.profile_products, response.data));
        this.reset_product_form();
        $("#manageProductModal").modal("hide");
        show_service_toast({
            icon: "success",
            text: "Service updated successfully!",
            timer: 2000,
        });
    },
};
